import React, { useState } from 'react';
import { GoogleGenAI } from '@google/genai';
import { BotConfig, VoiceName } from '../types';

interface ConfigPanelProps {
  config: BotConfig;
  setConfig: React.Dispatch<React.SetStateAction<BotConfig>>;
  onGenerateCode: () => void;
}

const VOICE_LABELS: Record<VoiceName, string> = {
  [VoiceName.Puck]: "Adam (Male)",
  [VoiceName.Charon]: "Marcus (Male)",
  [VoiceName.Kore]: "Sarah (Female)",
  [VoiceName.Fenrir]: "David (Male)",
  [VoiceName.Zephyr]: "Emily (Female)",
};

const ConfigPanel: React.FC<ConfigPanelProps> = ({ config, setConfig, onGenerateCode }) => {
  const [showGenerator, setShowGenerator] = useState(false);
  const [genUrl, setGenUrl] = useState('');
  const [genRole, setGenRole] = useState('');
  const [genApiKey, setGenApiKey] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [sheetInput, setSheetInput] = useState('');

  const handleChange = (field: keyof BotConfig, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSheet = () => {
    if (sheetInput.trim()) {
        const currentSheets = config.googleSheets || [];
        handleChange('googleSheets', [...currentSheets, sheetInput.trim()]);
        setSheetInput('');
    }
  };

  const handleRemoveSheet = (index: number) => {
    const currentSheets = config.googleSheets || [];
    const newSheets = currentSheets.filter((_, i) => i !== index);
    handleChange('googleSheets', newSheets);
  };

  const handleGenerateInstructions = async () => {
    if (!genUrl || !genRole) {
      setGenError("Please provide both a URL and a Role.");
      return;
    }
    
    // Use manual key if provided, otherwise fallback to env
    const apiKeyToUse = genApiKey || process.env.API_KEY;

    if (!apiKeyToUse) {
      setGenError("API Key is required to generate script.");
      return;
    }

    setIsGenerating(true);
    setGenError(null);

    try {
      const ai = new GoogleGenAI({ apiKey: apiKeyToUse });
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: `Analyze the website ${genUrl}. 
        Task: Create a detailed System Instruction script for an AI Voice/Text Agent.
        
        Agent Persona: ${genRole}
        Goal: Answer user questions about the website's business/content and generate leads.
        
        Requirements:
        1. Tone: Professional, helpful, and aligned with the website's brand.
        2. Knowledge Base: Summarize key services, products, or information likely found on this URL.
        3. Directives: concise answers for voice, friendly demeanor, asking qualifying questions for leads.
        4. Output format: Plain text, ready to be pasted into the 'System Instructions' field. Do not include markdown headers like '##' or '**'.`,
        config: {
          tools: [{ googleSearch: {} }], // Use Search to get actual info about the URL
        }
      });

      if (response.text) {
        setConfig(prev => ({ ...prev, systemInstruction: response.text }));
        setShowGenerator(false); // Close generator on success
      } else {
        setGenError("No response generated. Please try again.");
      }
    } catch (err: any) {
      console.error(err);
      setGenError(err.message || "Failed to generate instructions.");
    } finally {
      setIsGenerating(false);
    }
  };

  const voices = Object.values(VoiceName);

  return (
    <div className="w-full md:w-1/3 bg-white p-6 border-r border-gray-200 overflow-y-auto h-full flex flex-col">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Voice & Text Bot</h1>
        <p className="text-gray-500 text-sm">Design your conversational AI assistant for both voice and text interactions.</p>
      </div>

      <div className="space-y-6 flex-grow">
        {/* Name Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bot Name</label>
          <input
            type="text"
            value={config.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            placeholder="e.g. Sales Assistant"
          />
        </div>

        {/* Avatar URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Avatar URL</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={config.avatarUrl}
              onChange={(e) => handleChange('avatarUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              placeholder="https://..."
            />
            <div className="w-10 h-10 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-200">
               <img src={config.avatarUrl} alt="Preview" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        {/* Theme Color */}
        <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">Theme Color</label>
           <div className="flex items-center gap-3">
             <input
               type="color"
               value={config.themeColor}
               onChange={(e) => handleChange('themeColor', e.target.value)}
               className="h-10 w-20 p-1 border border-gray-300 rounded cursor-pointer"
             />
             <span className="text-gray-600 font-mono text-sm">{config.themeColor}</span>
           </div>
        </div>

        {/* Voice Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Voice Personality (Voice Mode)</label>
          <div className="grid grid-cols-1 gap-2">
            {voices.map((voice) => (
              <button
                key={voice}
                onClick={() => handleChange('voice', voice)}
                className={`px-4 py-3 rounded-lg text-sm font-medium border transition-all text-left flex justify-between items-center ${
                  config.voice === voice
                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span>{VOICE_LABELS[voice]}</span>
                {config.voice === voice && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Webhook URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL (Optional)</label>
          <input
            type="text"
            value={config.webhookUrl || ''}
            onChange={(e) => handleChange('webhookUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            placeholder="https://your-api.com/webhook"
          />
          <p className="text-xs text-gray-400 mt-1">
            If provided, the bot can call this URL with a JSON payload <code>{`{ "query": "..." }`}</code> to fetch real-time data.
          </p>
        </div>

        {/* Google Sheets Integration */}
        <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">Google Sheets Knowledge Base</label>
           <div className="flex gap-2 mb-2">
             <input
               type="text"
               value={sheetInput}
               onChange={(e) => setSheetInput(e.target.value)}
               className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
               placeholder="https://docs.google.com/spreadsheets/..."
             />
             <button 
               onClick={handleAddSheet}
               className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 rounded-lg border border-gray-200 text-sm font-medium transition"
             >
               Add
             </button>
           </div>
           
           {config.googleSheets && config.googleSheets.length > 0 && (
             <ul className="space-y-2 mt-2">
               {config.googleSheets.map((sheet, idx) => (
                 <li key={idx} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded text-xs text-green-800 border border-green-100">
                   <span className="truncate flex-1 mr-2">{sheet}</span>
                   <button onClick={() => handleRemoveSheet(idx)} className="text-green-600 hover:text-green-800">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                       <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                     </svg>
                   </button>
                 </li>
               ))}
             </ul>
           )}
           <p className="text-xs text-gray-400 mt-1">
             Add public Google Sheet links. The agent will use Search Grounding to read them (Text Mode only).
           </p>
        </div>

        {/* Preview URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preview Website URL (Dev Tool)</label>
          <input
            type="text"
            value={config.previewUrl || ''}
            onChange={(e) => handleChange('previewUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            placeholder="https://example.com"
          />
          <p className="text-xs text-gray-400 mt-1">
            Enter a URL to test how the widget looks on your site. Note: Some sites block iframe embedding via X-Frame-Options.
          </p>
        </div>

        {/* System Instructions */}
        <div>
          <div className="flex justify-between items-center mb-1">
             <label className="block text-sm font-medium text-gray-700">System Instructions</label>
             <button 
               onClick={() => setShowGenerator(!showGenerator)}
               className="text-xs text-indigo-600 font-medium flex items-center gap-1 hover:text-indigo-800"
             >
               <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
               </svg>
               {showGenerator ? 'Hide Generator' : 'Auto-Generate Script'}
             </button>
          </div>

          {/* Generator UI */}
          {showGenerator && (
            <div className="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100 space-y-3">
              <h4 className="text-xs font-bold text-indigo-800 uppercase tracking-wide">AI Script Generator</h4>
              <div>
                <label className="block text-xs font-medium text-indigo-700 mb-1">Target Website URL</label>
                <input 
                  type="text" 
                  value={genUrl}
                  onChange={(e) => setGenUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-2 py-1.5 text-sm border border-indigo-200 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-indigo-700 mb-1">Act as a... (Persona)</label>
                <input 
                  type="text" 
                  value={genRole}
                  onChange={(e) => setGenRole(e.target.value)}
                  placeholder="Sales Rep, Support Agent, etc."
                  className="w-full px-2 py-1.5 text-sm border border-indigo-200 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
              </div>
              
              {/* API KEY INPUT */}
              <div>
                <label className="block text-xs font-medium text-indigo-700 mb-1">Gemini API Key (Optional)</label>
                <input 
                  type="password" 
                  value={genApiKey}
                  onChange={(e) => setGenApiKey(e.target.value)}
                  placeholder="AIzaSy... (Leave blank to use default)"
                  className="w-full px-2 py-1.5 text-sm border border-indigo-200 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">
                   Required if you haven't set a default environment key.
                </p>
              </div>

              {genError && <p className="text-xs text-red-600 font-medium">{genError}</p>}
              <button 
                onClick={handleGenerateInstructions}
                disabled={isGenerating}
                className="w-full bg-indigo-600 text-white text-xs font-semibold py-2 rounded hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-wait flex justify-center items-center gap-2"
              >
                {isGenerating ? (
                   <>
                     <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                     Analyzing & Generating...
                   </>
                ) : (
                   '✨ Generate Instructions'
                )}
              </button>
            </div>
          )}

          <textarea
            value={config.systemInstruction}
            onChange={(e) => handleChange('systemInstruction', e.target.value)}
            className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none text-sm"
            placeholder="Define how the bot behaves, what it knows, and its tone..."
          />
          <p className="text-xs text-gray-400 mt-1">
            Tip: Be specific about the role (e.g., "You are a helpful travel agent specialized in Japan").
          </p>
        </div>
      </div>

      <div className="pt-6 border-t border-gray-100 mt-6">
        <button
          onClick={onGenerateCode}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
          </svg>
          Get Embed Code
        </button>
      </div>
    </div>
  );
};

export default ConfigPanel;